import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

/**
 * useIntersectionObserver Hook 配置选项
 */
type UseIntersectionObserverOptions = IntersectionObserverInit;

/**
 * 使用 Intersection Observer API 检测元素是否进入视口
 * 
 * @param ref - 要观察的元素引用
 * @param options - IntersectionObserver 配置选项
 * @returns 元素是否已进入视口
 * 
 * @example
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null);
 * const isVisible = useIntersectionObserver(ref, { threshold: 0.1 });
 * 
 * return (
 *   <div ref={ref} className={isVisible ? 'opacity-100' : 'opacity-0'}>
 *     内容
 *   </div>
 * );
 * ```
 */
export function useIntersectionObserver(
  ref: RefObject<Element>,
  options: UseIntersectionObserverOptions = {}
): boolean {
  // 降级方案：不支持 IntersectionObserver 时直接初始化为 true
  const [isVisible, setIsVisible] = useState(() => {
    return !('IntersectionObserver' in window);
  });

  useEffect(() => {
    // 如果不支持 IntersectionObserver，已经在初始化时设置为 true
    if (!('IntersectionObserver' in window)) {
      return;
    }

    const element = ref.current;
    if (!element) {
      return;
    }

    // 创建观察器实例
    const observer = new IntersectionObserver(
      ([entry]) => {
        // 当元素进入视口时
        if (entry.isIntersecting) {
          setIsVisible(true);
          // 元素进入视口后断开观察器（只触发一次）
          observer.disconnect();
        }
      },
      {
        threshold: 0.1, // 默认阈值：元素 10% 可见时触发
        ...options, // 允许覆盖默认配置
      }
    );

    // 开始观察元素
    observer.observe(element);

    // 清理函数：组件卸载时断开观察器
    return () => {
      observer.disconnect();
    };
  }, [ref, options]);

  return isVisible;
}
